import React from 'react';

/**
 * Calls `onPoll` on a fixed interval while `enabled` is true and the tab is visible. Polling pauses
 * when the tab is hidden and resumes when it becomes visible again. Mirrors the visibility-aware
 * polling used by the Batches search hook.
 *
 * The latest `onPoll` is held in a ref so changing its identity between renders does not restart the
 * interval — only `enabled` and `intervalMs` do.
 */
export function useVisibilityAwarePolling(enabled: boolean, intervalMs: number, onPoll: () => void): void {
  const onPollRef = React.useRef(onPoll);
  React.useEffect(() => {
    onPollRef.current = onPoll;
  }, [onPoll]);

  React.useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    function startPolling(): void {
      if (intervalId == null) {
        intervalId = setInterval(() => {
          onPollRef.current();
        }, intervalMs);
      }
    }

    function stopPolling(): void {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        startPolling();
      }
    }

    if (document.visibilityState !== 'hidden') {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, intervalMs]);
}
